import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Formik } from 'formik';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  DOWNLOAD_FISCAL_CAPITAL_BALANCE_WITH_CONTRIBUTION,
  SIMULATE_CAPITAL_CONTRIBUTION,
  downloadFiscalCapitalBalanceWithContributionAction,
  simulateCapitalContributionAction
} from 'src/store/actions/contractActions';
import MoneyMask from 'src/components/MoneyMask/MoneyMask';
import { formatCurrencyWithDecimals } from 'src/utils/formatAmout';
import useStyles from './CreateContributionForm/styles';
import makeInitialState from './capitalContributionSimulationInitialState';
import validationSchema from './capitalContributionSimulationSchema';

const CapitalContributionSimulationDialog = ({ open, onClose, contractId }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [result, setResult] = useState(null);

  const loadingSimulation = useSelector(
    isLoadingSelector([SIMULATE_CAPITAL_CONTRIBUTION.action])
  );
  const loadingDownload = useSelector(
    isLoadingSelector([DOWNLOAD_FISCAL_CAPITAL_BALANCE_WITH_CONTRIBUTION.action])
  );

  const initialValues = useMemo(() => makeInitialState(), []);

  const handleSubmitSimulation = useCallback(
    values => {
      setResult(null);
      dispatch(
        simulateCapitalContributionAction(contractId, values, data => {
          setResult(data);
        })
      );
    },
    [dispatch, contractId]
  );

  const handleDownload = useCallback(
    values => {
      dispatch(downloadFiscalCapitalBalanceWithContributionAction(contractId, values));
    },
    [dispatch, contractId]
  );

  const handleCloseDialog = useCallback(() => {
    setResult(null);
    onClose();
  }, [onClose]);

  if (!open) {
    return null;
  }

  return (
    <Dialog
      id="capital-contribution-simulation"
      classes={{ paper: classes.root }}
      open={open}
      scroll="body"
      keepMounted
      disableEscapeKeyDown
    >
      <DialogTitle
        className={classes.title}
        id="capital-contribution-simulation-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1
        }}
      >
        <Typography color="primary" variant="h2">
          {t('CONTRACT_VIEW.SIMULATE_CONTRIBUTION')}
        </Typography>
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={handleCloseDialog}
          sx={{ ml: 1 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          height: 'auto',
          maxHeight: 'calc(100vh - 200px)'
        }}
      >
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmitSimulation}
        >
          {({
            handleSubmit,
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isValid
          }) => {
            return (
              <>
                <form onSubmit={handleSubmit}>
                  <TextField
                    InputProps={{
                      autoComplete: 'off',
                      inputComponent: MoneyMask
                    }}
        InputLabelProps={{ shrink: true }}
                    error={Boolean(touched.amount && errors.amount)}
                    fullWidth
                    helperText={touched.amount && t(errors.amount)}
                    label={t('COMMON.AMOUNT_WITH_IVA')}
                    margin="normal"
                    name="amount"
                    value={values.amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                  />

                  <Box mt={2} mb={result ? 3 : 0}>
                    <Button
                      className={classes.buttons}
                      type="submit"
                      color="primary"
                      variant="contained"
                      fullWidth
                      disabled={loadingSimulation || !values.amount || !isValid}
                    >
                      {t('CONTRACT_VIEW.SIMULATE_CONTRIBUTION')}
                    </Button>
                  </Box>
                </form>

                {result && (
                  <>
                    <Table classes={{ root: classes.tableRoot }} component="div">
                      <TableBody classes={{ root: classes.tableBody }} component="div">
                        <TableRow component="div">
                          <TableCell
                            component="div"
                            className={classes.reviewDataLabel}
                          >
                            {t('CONTRACT_VIEW.RENT_AFTER_CONTRIBUTION')}
                          </TableCell>
                          <TableCell component="div">
                            <Typography variant="body2" color="textSecondary">
                              {formatCurrencyWithDecimals(
                                result.rentAfterContribution ?? 0
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow component="div">
                          <TableCell
                            component="div"
                            className={classes.reviewDataLabel}
                          >
                            {t('CONTRACT_VIEW.INTEREST_SAVINGS')}
                          </TableCell>
                          <TableCell component="div">
                            <Typography variant="body2" color="textSecondary">
                              {formatCurrencyWithDecimals(
                                result.interestSavings ?? 0
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow component="div">
                          <TableCell
                            component="div"
                            className={classes.reviewDataLabel}
                          >
                            {t(
                              'CONTRACT_VIEW.INTEREST_REMAINING_WITHOUT_CONTRIBUTION'
                            )}
                          </TableCell>
                          <TableCell component="div">
                            <Typography variant="body2" color="textSecondary">
                              {formatCurrencyWithDecimals(
                                result.interestRemainingWithoutContribution ?? 0
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow component="div">
                          <TableCell
                            component="div"
                            className={classes.reviewDataLabel}
                          >
                            {t('CONTRACT_VIEW.INTEREST_REMAINING_WITH_CONTRIBUTION')}
                          </TableCell>
                          <TableCell component="div">
                            <Typography variant="body2" color="textSecondary">
                              {formatCurrencyWithDecimals(
                                result.interestRemainingWithContribution ?? 0
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                      <Tooltip
                        title={t(
                          'CONTRACT_VIEW.EXPORT_FISCAL_CAPITAL_BALANCE_WITH_CONTRIBUTION'
                        )}
                      >
                        <span>
                          <Button
                            className={classes.buttons}
                            color="primary"
                            variant="contained"
                            onClick={() => handleDownload(values)}
                            disabled={loadingDownload}
                          >
                            {t('COMMON.DOWNLOAD')}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </>
                )}
              </>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

CapitalContributionSimulationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contractId: PropTypes.number.isRequired
};

export default CapitalContributionSimulationDialog;

